import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import styled from 'styled-components';
import { media } from '../../styles/theme';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 80px 24px 24px;

  ${media.phone} {
    padding: 72px 12px 24px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;

  h1 {
    color: #111827;
    font-size: 28px;
    font-weight: bold;
    margin: 0 0 8px 0;
  }

  p {
    color: #6b7280;
    font-size: 15px;
  }
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;

  h2 {
    color: #111827;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$single ? '1fr' : 'repeat(2, 1fr)'};
  gap: 20px;
  margin-bottom: ${props => props.$noMargin ? '0' : '20px'};

  /* 手机上一律单列，双列会挤 */
  ${media.phone} {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #374151;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;

  &.primary {
    background: #F1AF59;
    color: white;
    border-color: #F1AF59;

    &:hover {
      background: #e09c40;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
    }
  }

  &.danger {
    background: #ef4444;
    color: white;

    &:hover {
      background: #dc2626;
    }
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;

  &.success {
    background: #f0fdf4;
    color: #047857;
    border: 1px solid #86efac;
  }

  &.info {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #93c5fd;
  }

  &.warning {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }
`;

const Section = styled.div`
  &:not(:last-child) {
    margin-bottom: 32px;
  }
`;

const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const PreferenceLabel = styled.div`
  h3 {
    color: #111827;
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 4px 0;
  }

  p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e5e7eb;
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background: #F1AF59;
  }

  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const UserSettings = () => {
  const navigate = useNavigate();
  const { currentRole, currentUserId } = usePortalRole();
  const [showMessage, setShowMessage] = useState('');

  // Form states
  const [profile, setProfile] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '0412 345 678',
    address: '123 Main Street',
    city: 'Sydney',
    state: 'NSW',
    postcode: '2000',
    bio: 'Passionate educator with 10 years of experience.',
    timezone: 'Australia/Sydney',
    language: 'en'
  });

  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    marketingEmails: false,
    weeklyReports: true,
    paymentReminders: true
  });

  const handleProfileSave = () => {
    setShowMessage('profile');
    setTimeout(() => setShowMessage(''), 3000);
  };

  const handlePasswordChange = () => {
    if (password.new !== password.confirm) {
      alert('Passwords do not match!');
      return;
    }
    setShowMessage('password');
    setPassword({ current: '', new: '', confirm: '' });
    setTimeout(() => setShowMessage(''), 3000);
  };

  const handleNotificationSave = () => {
    setShowMessage('notifications');
    setTimeout(() => setShowMessage(''), 3000);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('portalDemoUser');
      navigate('/portal/login');
    }
  };

  return (
    <Container>
      <Header>
        <h1>Account Settings</h1>
        <p>Manage your account settings and preferences</p>
      </Header>

      {/* Profile Information */}
      <Section>
        <SettingsCard>
          <CardHeader>
            <h2>Profile Information</h2>
          </CardHeader>
          <CardContent>
            {showMessage === 'profile' && (
              <Alert className="success">
                Profile information has been updated successfully!
              </Alert>
            )}

            <FormGrid>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                />
              </FormGroup>
            </FormGrid>

            <FormGrid $single>
              <FormGroup>
                <Label>Street Address</Label>
                <Input
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>City</Label>
                <Input
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>State</Label>
                <Select
                  value={profile.state}
                  onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                >
                  <option value="NSW">New South Wales</option>
                  <option value="VIC">Victoria</option>
                  <option value="QLD">Queensland</option>
                  <option value="WA">Western Australia</option>
                  <option value="SA">South Australia</option>
                  <option value="TAS">Tasmania</option>
                  <option value="NT">Northern Territory</option>
                  <option value="ACT">Australian Capital Territory</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Postcode</Label>
                <Input
                  value={profile.postcode}
                  onChange={(e) => setProfile({ ...profile, postcode: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Timezone</Label>
                <Select
                  value={profile.timezone}
                  onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                >
                  <option value="Australia/Sydney">Sydney (UTC+10)</option>
                  <option value="Australia/Melbourne">Melbourne (UTC+10)</option>
                  <option value="Australia/Perth">Perth (UTC+8)</option>
                  <option value="Australia/Brisbane">Brisbane (UTC+10)</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGrid $single $noMargin>
              <FormGroup>
                <Label>Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                />
              </FormGroup>
            </FormGrid>

            <ButtonGroup>
              <Button className="secondary" onClick={() => window.location.reload()}>
                Cancel
              </Button>
              <Button className="primary" onClick={handleProfileSave}>
                Save Changes
              </Button>
            </ButtonGroup>
          </CardContent>
        </SettingsCard>
      </Section>

      {/* Change Password */}
      <Section>
        <SettingsCard>
          <CardHeader>
            <h2>Change Password</h2>
          </CardHeader>
          <CardContent>
            {showMessage === 'password' && (
              <Alert className="success">
                Password has been changed successfully!
              </Alert>
            )}

            <FormGrid $single>
              <FormGroup>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={password.current}
                  onChange={(e) => setPassword({ ...password, current: e.target.value })}
                  placeholder="Enter current password"
                />
              </FormGroup>

              <FormGroup>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={password.new}
                  onChange={(e) => setPassword({ ...password, new: e.target.value })}
                  placeholder="Enter new password"
                />
              </FormGroup>

              <FormGroup>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={password.confirm}
                  onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                  placeholder="Confirm new password"
                />
              </FormGroup>
            </FormGrid>

            <ButtonGroup>
              <Button className="primary" onClick={handlePasswordChange}>
                Update Password
              </Button>
            </ButtonGroup>
          </CardContent>
        </SettingsCard>
      </Section>

      {/* Notification Preferences */}
      <Section>
        <SettingsCard>
          <CardHeader>
            <h2>Notification Preferences</h2>
          </CardHeader>
          <CardContent>
            {showMessage === 'notifications' && (
              <Alert className="success">
                Notification preferences have been updated!
              </Alert>
            )}

            <PreferenceRow>
              <PreferenceLabel>
                <h3>Email Notifications</h3>
                <p>Receive important updates via email</p>
              </PreferenceLabel>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.emailNotifications}
                  onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                />
                <span />
              </Switch>
            </PreferenceRow>

            <PreferenceRow>
              <PreferenceLabel>
                <h3>SMS Notifications</h3>
                <p>Get text messages for urgent updates</p>
              </PreferenceLabel>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.smsNotifications}
                  onChange={(e) => setNotifications({ ...notifications, smsNotifications: e.target.checked })}
                />
                <span />
              </Switch>
            </PreferenceRow>

            <PreferenceRow>
              <PreferenceLabel>
                <h3>Push Notifications</h3>
                <p>Browser notifications for real-time updates</p>
              </PreferenceLabel>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.pushNotifications}
                  onChange={(e) => setNotifications({ ...notifications, pushNotifications: e.target.checked })}
                />
                <span />
              </Switch>
            </PreferenceRow>

            <PreferenceRow>
              <PreferenceLabel>
                <h3>Weekly Reports</h3>
                <p>Receive weekly summary reports</p>
              </PreferenceLabel>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.weeklyReports}
                  onChange={(e) => setNotifications({ ...notifications, weeklyReports: e.target.checked })}
                />
                <span />
              </Switch>
            </PreferenceRow>

            <PreferenceRow>
              <PreferenceLabel>
                <h3>Payment Reminders</h3>
                <p>Get reminded about upcoming payments</p>
              </PreferenceLabel>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.paymentReminders}
                  onChange={(e) => setNotifications({ ...notifications, paymentReminders: e.target.checked })}
                />
                <span />
              </Switch>
            </PreferenceRow>

            <ButtonGroup>
              <Button className="primary" onClick={handleNotificationSave}>
                Save Preferences
              </Button>
            </ButtonGroup>
          </CardContent>
        </SettingsCard>
      </Section>

      {/* Account Actions */}
      <Section>
        <SettingsCard>
          <CardHeader>
            <h2>Account Actions</h2>
          </CardHeader>
          <CardContent>
            <Alert className="info">
              Current Role: <strong>{currentRole.replace('_', ' ').toUpperCase()}</strong>
              {' '}| User ID: <strong>{currentUserId}</strong>
            </Alert>

            <ButtonGroup>
              <Button className="secondary" onClick={() => navigate('/portal')}>
                Back to Dashboard
              </Button>
              <Button className="danger" onClick={handleLogout}>
                Logout
              </Button>
            </ButtonGroup>
          </CardContent>
        </SettingsCard>
      </Section>
    </Container>
  );
};

export default UserSettings;